import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { useOverflowChips } from './useOverflowChips';
import { OverflowChipsMenu } from './OverflowChipsMenu';
import { FilterPills } from './FilterPills';
import type { FilterPill } from './InsightsSearchBar.types';

interface OverflowChipsContainerProps {
  pills: FilterPill[];
  onRemovePill: (pill: FilterPill) => void;
  onEditPill?: (pill: FilterPill) => void;
  editingPill?: FilterPill | null;
  editingValue?: string;
  onChangeEditingValue?: (value: string) => void;
  onSaveEditedPill?: (pill: FilterPill, newValue: string) => void;
  onCancelEdit?: () => void;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  searchBarRef?: React.RefObject<HTMLElement>;
}

// Fixed reserved space: padding(16) + gaps(8) + inputMinWidth(150) + gap(4) + clearBtn(40) + safety(8)
// The +N more chip appears before reaching the clear button
const RESERVED_WIDTH = 226;

export const OverflowChipsContainer = ({
  pills,
  onRemovePill,
  onEditPill,
  editingPill,
  editingValue,
  onChangeEditingValue,
  onSaveEditedPill,
  onCancelEdit,
  searchInputRef,
  searchBarRef,
}: OverflowChipsContainerProps): React.ReactElement => {
  const measureRef = useRef<HTMLDivElement>(null);
  const overflowChipRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const fallbackRef = useRef<HTMLElement>(null);
  const resolvedSearchBarRef = searchBarRef ?? fallbackRef;

  const { visibleCount, hiddenCount } = useOverflowChips(
    pills.length,
    resolvedSearchBarRef,
    measureRef,
    RESERVED_WIDTH
  );

  const visiblePills = useMemo(() => pills.slice(0, visibleCount), [pills, visibleCount]);
  const hiddenPills = useMemo(() => pills.slice(visibleCount), [pills, visibleCount]);
  const overflowLabel = useMemo(() => `+${hiddenCount} more`, [hiddenCount]);

  const handleOpenMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleRemovePill = useCallback(
    (pill: FilterPill): void => {
      onRemovePill(pill);
    },
    [onRemovePill]
  );

  const handleEditPill = useCallback(
    (pill: FilterPill): void => {
      onEditPill?.(pill);
      handleCloseMenu();
    },
    [onEditPill, handleCloseMenu]
  );

  return (
    <>
      <Box
        ref={measureRef}
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          display: 'flex',
          flexWrap: 'nowrap',
          gap: 0.5,
          left: 0,
          top: 0,
        }}
        aria-hidden="true"
      >
        {pills.map((pill, index) => (
          <Chip
            key={`measure-${pill.fieldKey}-${pill.value}-${index}`}
            label={`${pill.fieldName}: ${pill.displayValue}`}
            size="small"
            sx={{ mr: '0.25rem', flexShrink: 0 }}
          />
        ))}
      </Box>

      <FilterPills
        pills={visiblePills}
        onRemovePill={handleRemovePill}
        onEditPill={handleEditPill}
        editingPill={editingPill}
        editingValue={editingValue}
        onChangeEditingValue={onChangeEditingValue}
        onSaveEditedPill={onSaveEditedPill}
        onCancelEdit={onCancelEdit}
      />

      {hiddenCount > 0 && (
        <Box ref={overflowChipRef} sx={{ display: 'flex', flexShrink: 0 }}>
          <Chip
            label={overflowLabel}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenMenu();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleOpenMenu();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={`Show ${overflowLabel}`}
            sx={{
              backgroundColor: abyssColors.primary[100],
              color: abyssColors.primary[600],
              '& .MuiChip-label': { px: '0.5rem' },
              cursor: 'pointer',
              flexShrink: 0,
            }}
          />
        </Box>
      )}

      <OverflowChipsMenu
        open={isMenuOpen}
        anchorEl={overflowChipRef.current}
        hiddenPills={hiddenPills}
        onClose={handleCloseMenu}
        onRemovePill={handleRemovePill}
        onEditPill={handleEditPill}
        editingPill={editingPill}
        editingValue={editingValue}
        onChangeEditingValue={onChangeEditingValue}
        onSaveEditedPill={onSaveEditedPill}
        onCancelEdit={onCancelEdit}
      />
    </>
  );
};
